import type { ShopifyAjaxCartData } from "../../models/shopify/ajax-api/cart"
import type { TemplateIds } from "../../models/uv-inserts/template-ids"

/**
 * Normalises cart `_personalisation_choices` to an array for inspection.
 * Shopify Cart AJAX line item properties are string values; `_personalisation_choices` is a JSON string of the choices array.
 * Array input is still accepted for tests and any non-standard callers.
 */
function parsePersonalisationChoicesRaw(raw: unknown): unknown[] {
  if (typeof raw === "string" && raw.trim() !== "") {
    try {
      const parsed: unknown = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  if (Array.isArray(raw)) {
    return raw
  }
  return []
}

/**
 * Collects numeric variant IDs that appear in UV_INSERT choice rows (`c` is `configHandle:variantId`).
 */
function collectUvInsertVariantIdsFromChoices(choices: unknown[]): Set<string> {
  const ids = new Set<string>()
  for (const row of choices) {
    if (!row || typeof row !== "object") {
      continue
    }
    const rec = row as Record<string, unknown>
    const c = rec.c
    if (typeof c !== "string" || c.trim() === "") {
      continue
    }
    const parts = c.split(":")
    const tail = parts[parts.length - 1]
    if (/^\d+$/.test(tail)) {
      ids.add(tail)
    }
  }
  return ids
}

/**
 * Returns true when the cart line for `variantId` has personalisation choices that reference
 * a variant ID present in the UV inserts `templateIds` map (Made Mine insert already chosen).
 */
export function checkCartForUvInsert(options: {
  cart: ShopifyAjaxCartData
  variantId: string
  templateIds: TemplateIds
}): boolean {
  const { cart, variantId, templateIds } = options
  const target = String(variantId).trim()
  if (!target) {
    return false
  }

  const line = cart.items.find((item) => String(item.variant_id) === target)
  if (!line?.properties) {
    return false
  }

  const rawChoices = line.properties["_personalisation_choices"]
  const choices = parsePersonalisationChoicesRaw(rawChoices)
  if (choices.length === 0) {
    return false
  }

  const templateVariantKeys = new Set(
    Object.keys(templateIds).map((k) => String(k)),
  )
  if (templateVariantKeys.size === 0) {
    return false
  }

  const choiceVariantIds = collectUvInsertVariantIdsFromChoices(choices)
  for (const id of choiceVariantIds) {
    if (templateVariantKeys.has(id)) {
      return true
    }
  }

  return false
}
